import { API_BASE } from "./apiConfig";
import { ApiError, type TemplateFolder, type TemplateLinks } from "./types";

/**
 * Office SSO token handling.
 *
 * Tokens are cached in memory until shortly before expiry so that a task-pane
 * session doesn't ask Office for a fresh token on every call. We never persist
 * a token to sessionStorage or localStorage.
 */
let tokenCache: { token: string; expiresAt: number } | null = null;

const TOKEN_MARGIN_MS = 5 * 60_000;

function decodeExpiry(token: string): number {
  try {
    const part = token.split(".")[1];
    if (!part) return 0;
    const json = JSON.parse(
      atob(part.replace(/-/g, "+").replace(/_/g, "/"))
    ) as { exp?: number };
    return json.exp ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

async function getOfficeToken(): Promise<string> {
  if (tokenCache && Date.now() < tokenCache.expiresAt - TOKEN_MARGIN_MS) {
    return tokenCache.token;
  }

  let token: string;
  try {
    // Silent first — no prompt if consent already granted.
    token = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: false,
      allowConsentPrompt: false,
    });
  } catch {
    token = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
    });
  }

  tokenCache = { token, expiresAt: decodeExpiry(token) || Date.now() + 30 * 60_000 };
  return token;
}

/** Clears the cached token, e.g. after a 401, so the next call re-prompts. */
export function invalidateToken(): void {
  tokenCache = null;
}

type RequestOptions = {
  etag?: string;
  signal?: AbortSignal;
};

async function apiGet<T>(
  path: string,
  options: RequestOptions = {}
): Promise<{ data: T | null; etag: string | null; notModified: boolean }> {
  const token = await getOfficeToken();

  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (options.etag) headers["If-None-Match"] = options.etag;

  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers,
    signal: options.signal,
  });

  if (res.status === 304) {
    return { data: null, etag: options.etag ?? null, notModified: true };
  }

  if (!res.ok) {
    if (res.status === 401) invalidateToken();

    let message = `Request failed (${res.status})`;
    let correlationId: string | undefined;

    try {
      const body = (await res.json()) as { error?: string; correlationId?: string };
      if (body.error) message = body.error;
      correlationId = body.correlationId;
    } catch {
      // Non-JSON error body — keep the generic message.
    }

    throw new ApiError(message, res.status, correlationId);
  }

  return {
    data: (await res.json()) as T,
    etag: res.headers.get("etag"),
    notModified: false,
  };
}

/**
 * Folder tree, with ETag revalidation. On a repeat open the server answers 304
 * and we reuse the cached tree, so reopening the pane costs one tiny request.
 */
let treeCache: { tree: TemplateFolder; etag: string | null } | null = null;

export async function getTemplateTree(
  options: { forceRefresh?: boolean } = {}
): Promise<TemplateFolder> {
  const etag = options.forceRefresh ? undefined : treeCache?.etag ?? undefined;

  const result = await apiGet<{ tree: TemplateFolder }>("/templates/tree", { etag });

  if (result.notModified && treeCache) {
    return treeCache.tree;
  }

  if (!result.data) {
    throw new ApiError("Empty response from server", 502);
  }

  treeCache = { tree: result.data.tree, etag: result.etag };
  return result.data.tree;
}

export async function getTemplateLinks(id: string): Promise<TemplateLinks> {
  const result = await apiGet<TemplateLinks>(
    `/templates/${encodeURIComponent(id)}/links`
  );

  if (!result.data) throw new ApiError("Empty response from server", 502);
  return result.data;
}

/**
 * Fetches template bytes.
 *
 * Fast path: straight from the pre-authenticated SharePoint URL, which does not
 * touch our Function App at all. If that is blocked (some tenant network
 * configurations disallow it), fall back to the authenticated proxy endpoint.
 */
export async function fetchTemplateBytes(
  links: TemplateLinks,
  kind: "pptx" | "pdf",
  signal?: AbortSignal
): Promise<ArrayBuffer> {
  const directUrl = kind === "pptx" ? links.downloadUrl : links.pdfUrl;

  if (directUrl) {
    try {
      const res = await fetch(directUrl, { signal });
      if (res.ok) return await res.arrayBuffer();
    } catch {
      // Fall through to the proxy.
    }
  }

  const token = await getOfficeToken();
  const suffix = kind === "pdf" ? "?format=pdf" : "";

  const res = await fetch(
    `${API_BASE}/templates/${encodeURIComponent(links.id)}/content${suffix}`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );

  if (!res.ok) {
    if (res.status === 401) invalidateToken();
    throw new ApiError(`Could not download template (${res.status})`, res.status);
  }

  return res.arrayBuffer();
}
